import React, { useEffect, useState } from 'react';
import { Globe, Scale, Package, TrendingUp, Truck, DollarSign, ArrowRight } from 'lucide-react';
import { services, images } from '../mockData';

const iconMap = {
  Globe: Globe,
  Scale: Scale,
  Package: Package,
  TrendingUp: TrendingUp,
  Truck: Truck,
  DollarSign: DollarSign
};

const serviceImages = {
  1: images.services.cargoShip,
  2: images.services.businessMeeting,
  3: images.services.warehouse,
  4: images.services.financialChart,
  5: images.services.cargoShipAerial,
  6: images.services.financialChart
};

const Services = () => {
  const [scrollY, setScrollY] = useState(0);
  const [visibleCards, setVisibleCards] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);

    // Make all cards visible immediately for seamless experience
    setVisibleCards([0, 1, 2, 3, 4, 5]);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Bento grid layout configuration
  const bentoLayout = [
    { gridArea: '1 / 1 / 3 / 3', service: services[0] }, // Large 2x2 hero card
    { gridArea: '1 / 3 / 2 / 4', service: services[1] }, // 1x1
    { gridArea: '2 / 3 / 3 / 4', service: services[2] }, // 1x1
    { gridArea: '3 / 1 / 4 / 2', service: services[3] }, // 1x1
    { gridArea: '3 / 2 / 5 / 4', service: services[4] }, // 2x2
    { gridArea: '4 / 1 / 5 / 2', service: services[5] }  // 1x1
  ];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: 'var(--bg-primary)'
      }}>
        {/* Animated Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${images.hero.darkAbstract})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `translateY(${scrollY * 0.3}px) scale(1.1)`,
          transition: 'transform 0.1s ease-out',
          opacity: 0.2
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0,0,0,0.95), rgba(18,18,18,0.9))'
        }} />
        
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          padding: '0 7.6923%',
          maxWidth: '1000px'
        }}>
          <div style={{
            width: '100px',
            height: '2px',
            background: 'var(--brand-primary)',
            margin: '0 auto 40px',
            animation: 'drawLine 1.5s ease-out'
          }} />
          <h1 className="display-huge">Our Services</h1>
          <p className="body-large" style={{
            margin: '30px auto 0',
            maxWidth: '800px',
            color: 'var(--text-secondary)'
          }}>
            Comprehensive international trade solutions designed to elevate your global business operations. From market access to trade finance, we provide the expertise and support you need to succeed.
          </p>
        </div>
      </section>

      {/* Bento Grid Services Section */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-primary)',
        position: 'relative'
      }}>
        {/* Parallax Background Texture */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 100px), repeating-linear-gradient(-90deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02) 1px, transparent 1px, transparent 100px)',
          transform: `translateY(${scrollY * 0.1}px)`,
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Desktop Bento Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(4, 450px)',
            gap: '30px'
          }} className="bento-grid-desktop">
            {bentoLayout.map((item, index) => {
              const Icon = iconMap[item.service.icon];
              const isLarge = item.service.size === 'large';
              
              return (
                <div
                  key={index}
                  style={{
                    gridArea: item.gridArea,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    opacity: visibleCards.includes(index) ? 1 : 0,
                    transform: visibleCards.includes(index) ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
                    transition: 'all 0.6s ease-out'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(212, 175, 55, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-subtle)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Background Image */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${serviceImages[item.service.id]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.15,
                    transition: 'opacity 0.4s ease'
                  }} className="service-bg-image" />
                  
                  {/* Content */}
                  <div style={{
                    position: 'relative',
                    zIndex: 2,
                    height: '100%',
                    padding: isLarge ? '60px' : '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      {/* Icon with draw animation */}
                      <div style={{
                        width: isLarge ? '80px' : '60px',
                        height: isLarge ? '80px' : '60px',
                        background: 'rgba(212, 175, 55, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '24px',
                        opacity: visibleCards.includes(index) ? 1 : 0,
                        transition: 'opacity 0.6s ease 0.3s'
                      }}>
                        <Icon 
                          size={isLarge ? 40 : 32} 
                          color="var(--brand-primary)" 
                          strokeWidth={1.5} 
                        />
                      </div>
                      
                      <h3 style={{
                        fontFamily: 'Playfair Display, serif',
                        fontSize: isLarge ? '32px' : '24px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: 'var(--text-primary)'
                      }}>
                        {item.service.title}
                      </h3>
                      
                      <p style={{
                        fontSize: isLarge ? '18px' : '16px',
                        lineHeight: '1.7',
                        color: 'var(--text-secondary)',
                        marginBottom: '20px'
                      }}>
                        {item.service.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Waterfall Stack */}
          <div style={{
            display: 'none'
          }} className="bento-grid-mobile">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              
              return (
                <div
                  key={service.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    position: 'relative',
                    overflow: 'hidden',
                    marginBottom: '30px',
                    minHeight: '400px',
                    opacity: visibleCards.includes(index) ? 1 : 0,
                    transform: visibleCards.includes(index) ? 'translateY(0)' : 'translateY(30px)',
                    transition: 'all 0.6s ease-out'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${serviceImages[service.id]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.15
                  }} />
                  
                  <div style={{
                    position: 'relative',
                    zIndex: 2,
                    padding: '40px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'rgba(212, 175, 55, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '24px'
                      }}>
                        <Icon size={32} color="var(--brand-primary)" strokeWidth={1.5} />
                      </div>
                      
                      <h3 className="heading-2" style={{
                        marginBottom: '16px'
                      }}>
                        {service.title}
                      </h3>
                      
                      <p className="body-medium" style={{
                        marginBottom: '20px',
                        lineHeight: '1.7'
                      }}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) {
          .bento-grid-desktop {
            display: none !important;
          }
          .bento-grid-mobile {
            display: block !important;
          }
        }
        
        .service-card:hover .service-bg-image {
          opacity: 0.25;
        }
      `}</style>
    </div>
  );
};

export default Services;